/**
 * World chat (`backend/src/game/chat/`).
 *
 * ─── Why history is fetched and lines are pushed ────────────────────────────
 * `GET /chat/world` fills the window when it opens (and after a reconnect
 * drops whatever was pushed while the socket was down); `chat:message` appends
 * live. The two are reconciled by id, not by position — a line can arrive both
 * ways when a fetch and a push race, and a chat that shows the same message
 * twice looks broken in a way nothing logs.
 *
 * ─── The buffer is bounded ──────────────────────────────────────────────────
 * A session left open all evening with bots talking would otherwise grow an
 * unbounded array and an unbounded number of DOM rows. `MAX_MESSAGES` is the
 * cap; the window scrolls, so older lines are not reachable anyway.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';
import { socket } from '../../core/network/socket';
import type { ChatHistoryView, ChatMessageView, ChatState } from './chat-state.types';

/** Roughly a screen and a half of scrollback at the panel's height. */
export const MAX_MESSAGES = 200;

const INITIAL_STATE: ChatState = {
  messages: [], open: false, unread: 0, loading: false, error: null,
};

export const chatStateStore = createStore<ChatState>({ ...INITIAL_STATE });

let wired = false;

export function connectChat(): () => void {
  if (wired) return () => {};
  wired = true;

  const onMessage = (message: ChatMessageView) => appendMessage(message);
  const onReconnect = () => { void loadChatHistory(); };

  socket.on('chat:message', onMessage);
  socket.on('connect', onReconnect);

  void loadChatHistory();

  return () => {
    socket.off('chat:message', onMessage);
    socket.off('connect', onReconnect);
    wired = false;
  };
}

/**
 * Appends one line, de-duplicated by id.
 *
 * The sender gets their own message back twice by design — once as the
 * handler's direct reply (carrying `self: true`) and once through the
 * broadcast everyone receives. The id match is what collapses them, and the
 * `self` flag from whichever arrived first is kept, since only the direct
 * reply ever sets it.
 */
export function appendMessage(message: ChatMessageView): void {
  chatStateStore.set((s) => {
    const existing = s.messages.findIndex((m) => m.id === message.id);
    if (existing !== -1) {
      const merged = [...s.messages];
      merged[existing] = { ...message, self: merged[existing]!.self || message.self };
      return { ...s, messages: merged };
    }
    const messages = [...s.messages, message].slice(-MAX_MESSAGES);
    return { ...s, messages, unread: s.open ? 0 : s.unread + 1 };
  });
}

export async function loadChatHistory(): Promise<void> {
  chatStateStore.set((s) => ({ ...s, loading: true, error: null }));
  try {
    const history = await api.get<ChatHistoryView>('/chat/world');
    chatStateStore.set((s) => {
      // Merge rather than replace: a push that landed during the request must
      // not be thrown away by the response that raced it.
      const byId = new Map(history.messages.map((m) => [m.id, m]));
      for (const m of s.messages) if (!byId.has(m.id)) byId.set(m.id, m);
      const messages = [...byId.values()]
        .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
        .slice(-MAX_MESSAGES);
      return { ...s, messages, loading: false };
    });
  } catch {
    chatStateStore.set((s) => ({ ...s, loading: false, error: 'Chat is unavailable.' }));
  }
}

export function sendChat(content: string): void {
  const trimmed = content.trim();
  if (!trimmed) return;
  socket.emit('chat:send', { content: trimmed });
}

export function openChat(): void {
  chatStateStore.set((s) => ({ ...s, open: true, unread: 0 }));
}

export function closeChat(): void {
  chatStateStore.set((s) => ({ ...s, open: false }));
}

export function toggleChat(): void {
  if (chatStateStore.get().open) closeChat();
  else openChat();
}

/**
 * Janeks: *"Show message time."*
 *
 * `HH:MM` in the VIEWER's locale and zone, from the ISO instant the server
 * sent. Formatting server-side would stamp every client with the server's
 * timezone, which for anyone outside it makes a live conversation look hours
 * old.
 */
export function formatMessageTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function resetChat(): void {
  chatStateStore.set({ ...INITIAL_STATE });
}
